import { useState, useEffect, useRef } from 'react';
import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CreatableCombobox } from '@/components/ui/creatable-combobox';
import { Plus, Edit, Trash2, ChevronDown, ChevronUp, Link, Calendar, MessageSquare, X, LayoutGrid, TableIcon, Search, FileText } from 'lucide-react';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { STATUS_OPTIONS, STATUS_CONFIG, DEFAULT_STATUS, type StakeholderStatus } from '@/constants/stakeholderStatus';
import { usePagination } from '@/hooks/usePagination';
import { 
  Pagination, 
  PaginationContent, 
  PaginationItem, 
  PaginationLink, 
  PaginationNext, 
  PaginationPrevious,
  PaginationEllipsis
} from '@/components/ui/pagination';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DataTable, buildEditAction, buildDeleteAction } from '@/components/common/DataTable';
import { useStakeholders, useDeleteStakeholder } from '@/hooks/useStakeholdersData';
import { useOptimizedStakeholderQueries } from '@/hooks/useOptimizedStakeholderQueries';
import { useConsolidatedStakeholderPermissions } from '@/hooks/useConsolidatedStakeholderPermissions';
import { StakeholderAddDialog } from '@/components/admin/StakeholderAddDialog';
import { StakeholderEditDialog } from '@/components/admin/StakeholderEditDialog';
import { StakeholderThumbnail } from '@/components/stakeholders/StakeholderThumbnail';
import { StakeholderImageUploadDialog } from '@/components/stakeholders/StakeholderImageUploadDialog';
import { StakeholderGridView } from '@/components/stakeholders/StakeholderGridView';
import { useDialogStatePersisted } from '@/hooks/useDialogStatePersisted';
import { useStakeholdersViewPermissions, useStakeholdersEditPermissions } from '@/hooks/useStakeholdersPermissions';
import { useLocalStorageManager } from '@/hooks/useLocalStorageManager';
import type { Stakeholder } from '@/pages/admin/StakeholderManagement';
import Header from '@/components/layout/Header';
import { useGlobalFeatureFlag } from '@/lib/featureFlags';
import { useAuth } from '@/hooks/useAuth';

import { supabase } from '@/integrations/supabase/client';
import { useStakeholderInteractions, useStakeholderFollowUps } from '@/hooks/useStakeholderInteractions';
import { calculateMetrics } from '@/lib/crm/utils';
import { MetricsCards } from '@/components/stakeholders/dashboard/MetricsCards';
import { StakeholderCalendar } from '@/components/stakeholders/calendar/StakeholderCalendar';
import { StakeholderEventsList } from '@/components/stakeholders/calendar/StakeholderEventsList';
import { LogInteractionDialog } from '@/components/stakeholders/dialogs/LogInteractionDialog';
import { AddFollowUpDialog } from '@/components/stakeholders/dialogs/AddFollowUpDialog';
import { EditFollowUpDialog } from '@/components/stakeholders/dialogs/EditFollowUpDialog';
import { EditInteractionDialog } from '@/components/stakeholders/dialogs/EditInteractionDialog';
import { StakeholderAssignDialog } from '@/components/admin/StakeholderAssignDialog';
import { DeleteConfirmDialog } from '@/components/common/ConfirmDialog';
import type { CRMStakeholder, CRMInteraction, CRMFollowUp } from '@/lib/crm/types';
import { toast } from 'sonner';
import { EventsTable } from '@/components/stakeholders/EventsTable';
import { useDeleteInteraction, useDeleteFollowUp } from '@/hooks/useStakeholderInteractions';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

// URL param helpers
const parseArrayParam = (param: string | null): string[] => {
  return param ? param.split(',').filter(Boolean) : [];
};

const arrayToParam = (arr: string[]): string | undefined => {
  return arr.length > 0 ? arr.join(',') : undefined;
};

export default function Stakeholders() {
  const queryClient = useQueryClient();
  const { session } = useAuth();
  const { getStandardValue, setStandardValue, removeStandardValue } = useLocalStorageManager();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Parse initial state from URL
  const initialTab = (searchParams.get('tab') as 'stakeholders' | 'events') || 'stakeholders';
  const initialView = (searchParams.get('view') as 'table' | 'grid') || 
    getStandardValue('home-stakeholders-view-mode', 'grid');
  const initialPage = parseInt(searchParams.get('page') || '1', 10) || 1;
  const initialLeads = parseArrayParam(searchParams.get('leads'));
  const initialTypes = parseArrayParam(searchParams.get('types'));
  const initialStatuses = parseArrayParam(searchParams.get('statuses'));
  
  // Persistence keys for selected stakeholder
  const EDIT_SELECTION_KEY = 'home-edit-selected-stakeholder-id';
  const ASSIGN_SELECTION_KEY = 'home-assign-selected-stakeholder-id';
  
  const [selectedStakeholder, setSelectedStakeholder] = useState<Stakeholder | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [stakeholderToDelete, setStakeholderToDelete] = useState<Stakeholder | null>(null);
  const [editingFollowUp, setEditingFollowUp] = useState<CRMFollowUp | null>(null);
  const [editingInteraction, setEditingInteraction] = useState<CRMInteraction | null>(null);
  const [activeTab, setActiveTab] = useState<'stakeholders' | 'events'>(initialTab);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [noteViewStakeholder, setNoteViewStakeholder] = useState<Stakeholder | null>(null);
  
  // Image upload dialog state
  const [imageUploadOpen, setImageUploadOpen] = useState(false);
  const [imageUploadStakeholder, setImageUploadStakeholder] = useState<Stakeholder | null>(null);
  
  // View mode state (table or grid)
  const [viewMode, setViewMode] = useState<'table' | 'grid'>(initialView);
  
  // Persisted dialog states
  const addDialog = useDialogStatePersisted('home-stakeholder-add-dialog');
  const editDialog = useDialogStatePersisted('home-stakeholder-edit-dialog');
  const editFollowUpDialog = useDialogStatePersisted('home-stakeholder-edit-followup');
  const editInteractionDialog = useDialogStatePersisted('home-stakeholder-edit-interaction');
  
  // Refs for restoration tracking
  const hasRestoredEditRef = useRef(false);
  const hasRestoredAssignRef = useRef(false);
  
  // Persisted state for log/followup/assign/event dialogs
  const [isLogDialogOpen, setIsLogDialogOpen] = useState(() => 
    getStandardValue('home-log-interaction-dialog', false)
  );
  const [isFollowUpDialogOpen, setIsFollowUpDialogOpen] = useState(() =>
    getStandardValue('home-add-followup-dialog', false)
  );
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(() =>
    getStandardValue('home-assign-dialog', false)
  );
  const [isAddEventDialogOpen, setIsAddEventDialogOpen] = useState(() =>
    getStandardValue('home-add-event-dialog', false)
  );
  const [prefilledDate, setPrefilledDate] = useState<Date | undefined>(() => {
    const stored = getStandardValue<string>('home-prefilled-date', '');
    return stored ? new Date(stored) : undefined;
  });
  
  // Filter states - initialized from URL
  const [filterLeads, setFilterLeads] = useState<string[]>(initialLeads);
  const [filterTypes, setFilterTypes] = useState<string[]>(initialTypes);
  const [filterStatuses, setFilterStatuses] = useState<string[]>(initialStatuses);
  
  // Global search state
  const [searchQuery, setSearchQuery] = useState<string>(searchParams.get('q') || '');
  
  // Inline editing state
  const [editingCell, setEditingCell] = useState<{
    stakeholderId: string;
    field: string;
    value: any;
  } | null>(null);
  const [editingValue, setEditingValue] = useState<string>('');
  
  // Options for creatable comboboxes
  const [projectGroups, setProjectGroups] = useState<string[]>([]);
  const [projectTypes, setProjectTypes] = useState<string[]>([]);
  
  // Persist dialog states on change
  useEffect(() => {
    setStandardValue('home-log-interaction-dialog', isLogDialogOpen);
  }, [isLogDialogOpen, setStandardValue]);
  
  useEffect(() => {
    setStandardValue('home-add-followup-dialog', isFollowUpDialogOpen);
  }, [isFollowUpDialogOpen, setStandardValue]);
  
  useEffect(() => {
    setStandardValue('home-assign-dialog', isAssignDialogOpen);
  }, [isAssignDialogOpen, setStandardValue]);
  
  useEffect(() => {
    setStandardValue('home-add-event-dialog', isAddEventDialogOpen);
  }, [isAddEventDialogOpen, setStandardValue]);
  
  useEffect(() => {
    if (prefilledDate) {
      setStandardValue('home-prefilled-date', prefilledDate.toISOString());
    }
  }, [prefilledDate, setStandardValue]);
  
  // Persist view mode
  useEffect(() => {
    setStandardValue('home-stakeholders-view-mode', viewMode);
  }, [viewMode, setStandardValue]);

  // ✅ Scroll position preservation
  const SCROLL_POSITION_KEY = 'home-stakeholders-scroll-position';
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  
  // Save scroll position before unmount (declared early, runs on cleanup)
  useEffect(() => {
    return () => {
      if (window.scrollY > 0) {
        sessionStorage.setItem(SCROLL_POSITION_KEY, String(window.scrollY));
      }
    };
  }, []);

  // ✅ Phase 1: Use optimized queries only (remove dual fetching)
  const isPermissionsOptimized = useGlobalFeatureFlag('ENABLE_CONSOLIDATED_STAKEHOLDER_PERMISSIONS');
  // ✅ HARDCODED 2026-04-30 (PR-E): ENABLE_STAKEHOLDER_CRM_DASHBOARD retired — dashboard always on
  
  // Data fetching: Use optimized queries only (Phase 2 Week 6.2: no default [])
  const { 
    data: stakeholders, 
    isLoading, 
    isError, 
    error, 
    refetch,
    status,         // 'pending' | 'success' | 'error'
    fetchStatus,    // 'idle' | 'fetching' | 'paused'
    dataUpdatedAt   // timestamp number
  } = useOptimizedStakeholderQueries();
  
  // Fetch interactions and follow-ups for dashboard
  const stakeholderIds = stakeholders?.map((s: any) => s.id).filter(Boolean) ?? [];
  const { data: interactions = [], refetch: refetchInteractions } = useStakeholderInteractions(
    stakeholderIds
  );
  const { data: followUps = [], refetch: refetchFollowUps } = useStakeholderFollowUps(
    stakeholderIds
  );

  // Convert to CRM types for dashboard
  const crmStakeholders: CRMStakeholder[] = React.useMemo(() => {
    if (!stakeholders) return [];
    return (stakeholders as any[]).map((s: any) => ({
      id: s.id,
      name: s.name,
      email: s.email,
      phone: s.phone,
      role: s.role as any,
      project: s.project_name, // Use project_name from RPC
      notes: s.notes,
      lastContact: undefined,
      nextFollowUp: s.next_follow_up || s.nextFollowUp
    }));
  }, [stakeholders]);

  // Calculate dashboard metrics
  const metrics = React.useMemo(() =>
    calculateMetrics(crmStakeholders, interactions as CRMInteraction[], followUps as CRMFollowUp[]),
    [crmStakeholders, interactions, followUps]
  );

  // ✅ Restore scroll position (after data loads)
  useEffect(() => {
    if (isLoading || !stakeholders?.length) return;
    
    const savedPosition = sessionStorage.getItem(SCROLL_POSITION_KEY);
    if (savedPosition) {
      requestAnimationFrame(() => {
        window.scrollTo(0, parseInt(savedPosition, 10));
      });
      sessionStorage.removeItem(SCROLL_POSITION_KEY);
    }
  }, [isLoading, stakeholders?.length]);
  
  // Restore selected stakeholder for Edit dialog
  useEffect(() => {
    if (editDialog.isOpen && !selectedStakeholder && !hasRestoredEditRef.current) {
      const savedId = getStandardValue<string>(EDIT_SELECTION_KEY, '');
      
      // Only process after data has loaded
      if (!isLoading && savedId) {
        if (Array.isArray(stakeholders) && stakeholders.length > 0) {
          const found = (stakeholders as any[]).find(s => s.id === savedId);
          if (found) {
            setSelectedStakeholder(found);
            hasRestoredEditRef.current = true;
            return;
          }
        }
        
        // Only close dialog if data loaded AND not found
        console.warn('[Edit Dialog] Stakeholder not found, closing dialog:', savedId);
        removeStandardValue(EDIT_SELECTION_KEY);
        editDialog.close();
        hasRestoredEditRef.current = true;
      }
      
      // If still loading: wait for data
    }
    if (!editDialog.isOpen) {
      hasRestoredEditRef.current = false;
    }
  }, [editDialog.isOpen, stakeholders, selectedStakeholder, isLoading, getStandardValue, removeStandardValue, editDialog]);
  
  // Restore selected stakeholder for Assign dialog
  useEffect(() => {
    if (isAssignDialogOpen && !selectedStakeholder && !hasRestoredAssignRef.current) {
      const savedId = getStandardValue<string>(ASSIGN_SELECTION_KEY, '');
      
      // Only process after data has loaded
      if (!isLoading && savedId) {
        if (Array.isArray(stakeholders) && stakeholders.length > 0) {
          const found = (stakeholders as any[]).find(s => s.id === savedId);
          if (found) {
            setSelectedStakeholder(found);
            hasRestoredAssignRef.current = true;
            return;
          }
        }
        
        // Only close dialog if data loaded AND not found
        console.warn('[Assign Dialog] Stakeholder not found, closing dialog:', savedId);
        removeStandardValue(ASSIGN_SELECTION_KEY);
        setIsAssignDialogOpen(false);
        hasRestoredAssignRef.current = true;
      }
      
      // If still loading: wait for data
    }
    if (!isAssignDialogOpen) {
      hasRestoredAssignRef.current = false;
    }
  }, [isAssignDialogOpen, stakeholders, selectedStakeholder, isLoading, getStandardValue, removeStandardValue]);

  const tableData = React.useMemo(() => {
    if (!stakeholders) return [];
    return (stakeholders as any[]).map((s: any) => ({
      ...s,
      project_name: s.project_name || null, // Use project_name directly from RPC
    }));
  }, [stakeholders]);

  // Apply filters - multi-select + global search
  const filteredData = React.useMemo(() => {
    return tableData.filter(stakeholder => {
      // Global search - search in all fields
      if (searchQuery) {
        const query = searchQuery.toLowerCase().trim();
        const searchableFields = [
          stakeholder.name,
          stakeholder.email,
          stakeholder.phone,
          stakeholder.project_name,
          stakeholder.project_group,
          stakeholder.project_type,
          stakeholder.notes,
          stakeholder.creator_name,
          stakeholder.role
        ].filter(Boolean).join(' ').toLowerCase();
        
        if (!searchableFields.includes(query)) {
          return false;
        }
      }
      
      // Filter by Leads (empty array = show all)
      if (filterLeads.length > 0 && !filterLeads.includes(stakeholder.created_by || '')) {
        return false;
      }
      
      // Filter by Types (empty array = show all)
      if (filterTypes.length > 0 && !filterTypes.includes(stakeholder.project_type || '')) {
        return false;
      }
      
      // Filter by Statuses (empty array = show all)
      if (filterStatuses.length > 0 && !filterStatuses.includes(stakeholder.status ?? 'hold')) {
        return false;
      }
      
      return true;
    });
  }, [tableData, searchQuery, filterLeads, filterTypes, filterStatuses]);

  // Setup pagination with initialPage from URL - page size 40
  const pagination = usePagination(filteredData.length, { 
    pageSize: 40, 
    initialPage: initialPage
  });

  // Generate page numbers with ellipsis for better UX
  const generatePageNumbers = (): (number | 'ellipsis')[] => {
    const pages: (number | 'ellipsis')[] = [];
    
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1); // Always show first page
      
      if (currentPage <= 4) {
        for (let i = 2; i <= 5; i++) pages.push(i);
        pages.push('ellipsis');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push('ellipsis');
        for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push('ellipsis');
        pages.push(currentPage - 1, currentPage, currentPage + 1);
        pages.push('ellipsis');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  const { currentPage, totalPages, startItem, endItem, setPage, hasNextPage, hasPreviousPage } = pagination;

  // Sync state to URL (debounced)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const params = new URLSearchParams();
      
      // Tab (default: stakeholders → omit)
      if (activeTab !== 'stakeholders') {
        params.set('tab', activeTab);
      }
      
      // View mode (default: grid → omit)
      if (viewMode !== 'grid') {
        params.set('view', viewMode);
      }
      
      // Filters (arrays)
      const leadsParam = arrayToParam(filterLeads);
      const typesParam = arrayToParam(filterTypes);
      const statusesParam = arrayToParam(filterStatuses);
      
      if (leadsParam) params.set('leads', leadsParam);
      if (typesParam) params.set('types', typesParam);
      if (statusesParam) params.set('statuses', statusesParam);
      
      // Search query
      if (searchQuery) params.set('q', searchQuery);
      
      // Pagination (default: 1 → omit)
      if (currentPage > 1) {
        params.set('page', String(currentPage));
      }
      
      setSearchParams(params, { replace: true });
    }, 300);
    
    return () => clearTimeout(timeoutId);
  }, [activeTab, viewMode, filterLeads, filterTypes, filterStatuses, searchQuery, currentPage, setSearchParams]);

  // sync total items
  useEffect(() => {
    pagination.setTotalItems(filteredData.length);
  }, [filteredData.length]);

  // clamp chỉ khi thực sự vượt range (totalPages tươi, không chạy lúc loading)
  useEffect(() => {
    if (isLoading) return;
    if (filteredData.length > 0 && totalPages >= 1 && currentPage > totalPages) {
      pagination.setPage(1);
    }
  }, [isLoading, currentPage, totalPages, filteredData.length]);

  // Reset to page 1 when filters change
  const filterKey = `${filterLeads.join()}-${filterTypes.join()}-${filterStatuses.join()}-${searchQuery}`;
  const prevFilterKeyRef = useRef(filterKey);
  useEffect(() => {
    if (prevFilterKeyRef.current !== filterKey) {
      prevFilterKeyRef.current = filterKey;
      if (currentPage !== 1) {
        pagination.setPage(1);
      }
    }
  }, [filterKey, currentPage]);

  // Diagnostic log (DEV only)
  if (import.meta.env.DEV) {
    console.debug('[Stakeholders pagination]', {
      totalLen: tableData.length,
      filteredLen: filteredData.length,
      currentPage,
      totalPages,
      startItem,
      endItem,
      status,
      fetchStatus,
      dataUpdatedAt,
      isLoading,
      isError
    });
  }

  // 🔧 FIX: Protect slice operation from invalid indices
  const safeStart = Math.max(0, startItem - 1);
  const safeEnd = endItem === 0 
    ? Math.min(pagination.pageSize, filteredData.length)
    : endItem;
    
  const paginatedData = React.useMemo(() => {
    return filteredData.slice(safeStart, safeEnd);
  }, [filteredData, safeStart, safeEnd]);

  // Reset pagination when data changes to prevent showing empty page
  useEffect(() => {
    // Skip if no data yet
    if ((stakeholders ?? []).length === 0) return;

    // Only reset if current page exceeds total pages
    if (currentPage > totalPages && totalPages > 0) {
      setPage(totalPages);
    }
  }, [stakeholders, filteredData.length, currentPage, totalPages, setPage]);

  // Init deletion mutation
  const deleteStakeholder = useDeleteStakeholder();
  const { mutateAsync: deleteInteraction } = useDeleteInteraction();
  const { mutateAsync: deleteFollowUp } = useDeleteFollowUp();

  // Fetch distinct project_group and project_type values for combobox options
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const { data: projectsData } = await supabase
          .from('projects')
          .select('project_group, project_type');
        
        const { data: stakeholdersData } = await supabase
          .from('stakeholders')
          .select('project_group, project_type');

        const allData = [...(projectsData || []), ...(stakeholdersData || [])];
        
        const uniqueGroups = Array.from(new Set(
          allData.map(d => d.project_group).filter(Boolean)
        )).sort() as string[];
        
        const uniqueTypes = Array.from(new Set(
          allData.map(d => d.project_type).filter(Boolean)
        )).sort() as string[];

        setProjectGroups(uniqueGroups);
        setProjectTypes(uniqueTypes);
      } catch (error) {
        console.error('Failed to fetch options:', error);
      }
    };

    fetchOptions();
  }, []);

  // Permissions: Use consolidated or legacy based on flag
  const legacyViewPerms = useStakeholdersViewPermissions();
  const legacyEditPerms = useStakeholdersEditPermissions();
  const consolidatedPerms = useConsolidatedStakeholderPermissions();

  const { canViewList, canCreate, canEdit, canDelete } = isPermissionsOptimized
    ? consolidatedPerms
    : {
        canViewList: legacyViewPerms.list,
        canCreate: legacyEditPerms.create,
        canEdit: legacyEditPerms.edit,
        canDelete: legacyEditPerms.delete
      };

  const handleEdit = (stakeholder: Stakeholder) => {
    if (!canEdit) return;
    setSelectedStakeholder(stakeholder);
    setStandardValue(EDIT_SELECTION_KEY, stakeholder.id);
    editDialog.open();
  };

  const handleDelete = (stakeholder: Stakeholder) => {
    if (!canDelete) return;
    setStakeholderToDelete(stakeholder);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!stakeholderToDelete) return;
    
    try {
      if (stakeholderToDelete.assignment_id) {
        // Assigned stakeholder: delete assignment only
        await deleteStakeholder.mutateAsync(stakeholderToDelete.assignment_id);
      } else {
        // Unassigned stakeholder: delete from stakeholders table
        const { error } = await supabase
          .from('stakeholders')
          .delete()
          .eq('id', stakeholderToDelete.id);
        
        if (error) throw error;
      }
      
      toast.success('Stakeholder deleted');
      refetch();
    } catch (error: any) {
      toast.error('Failed to delete stakeholder', {
        description: error.message
      });
    } finally {
      setDeleteDialogOpen(false);
      setStakeholderToDelete(null);
    }
  };

  const handleCloseEdit = () => {
    removeStandardValue(EDIT_SELECTION_KEY);
    setSelectedStakeholder(null);
    editDialog.close();
  };
  
  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date);
  };
  
  // Inline edit handlers
  const handleCellClick = (stakeholder: Stakeholder, field: string) => {
    if (!canEdit) return;
    
    // Base editable fields (including status and notes)
    let editableFields = ['name', 'email', 'phone', 'role', 'status', 'notes'];
    
    // Add project_group, project_type, and project only if no linked project assigned
    if (!stakeholder.project_id) {
      editableFields.push('project_group', 'project_type', 'project');
    }
    
    if (!editableFields.includes(field)) return;

    setEditingCell({ stakeholderId: stakeholder.id, field, value: (stakeholder as any)[field] });
    // For 'project' field, use text_project from mapped data
    if (field === 'project') {
      setEditingValue((stakeholder as any).text_project || '');
    } else {
      setEditingValue((stakeholder as any)[field] || '');
    }
  };

  // Optimistic update helper
  const updateStakeholderCache = (stakeholderId: string, field: string, newValue: any) => {
    queryClient.setQueryData(
      ['stakeholders', 'optimized', 'v10-unified-rpc', 'on', session?.access_token?.slice(-12) || 'anon'],
      (old: Stakeholder[] | undefined) =>
        old?.map(s => s.id === stakeholderId ? { ...s, [field]: newValue } : s)
    );
  };

  const handleCellBlur = async () => {
    if (!editingCell) return;

    const { stakeholderId, field, value: originalValue } = editingCell;
    
    // Skip auto-save for combobox fields and status (they use handleComboboxConfirm/handleStatusChange)
    if (field === 'project_group' || field === 'project_type' || field === 'status') {
      return;
    }
    
    // No change, just cancel
    if (editingValue === originalValue) {
      setEditingCell(null);
      return;
    }

    // Prevent editing project_group or project_type if project is assigned
    const stakeholder = stakeholders?.find((s: any) => s.id === stakeholderId);
    if ((field === 'project_group' || field === 'project_type') && stakeholder?.project_id) {
      toast.error('Cannot edit group/type when project is assigned');
      setEditingCell(null);
      return;
    }

    try {
      // Update stakeholder
      const updatePayload: any = { [field]: editingValue || null };
      
      // For role field, also update assignment
      if (field === 'role') {
        if (stakeholder?.project_id && stakeholder?.assignment_id) {
          await supabase
            .from('project_stakeholder_assignments')
            .update({ role: editingValue || 'Stakeholder' })
            .eq('id', stakeholder.assignment_id);
        }
      }

      const { error } = await supabase
        .from('stakeholders')
        .update(updatePayload)
        .eq('id', stakeholderId);

      if (error) throw error;

      // Optimistic update instead of refetch
      // For 'project' field, update 'text_project' in cache (mapped key)
      if (field === 'project') {
        updateStakeholderCache(stakeholderId, 'text_project', editingValue || null);
      } else {
        updateStakeholderCache(stakeholderId, field, editingValue || null);
      }
      toast.success('Updated successfully');
    } catch (error) {
      console.error('Inline edit error:', error);
      toast.error('Failed to update');
    } finally {
      setEditingCell(null);
    }
  };

  const handleCellKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      (e.target as HTMLInputElement).blur();
    } else if (e.key === 'Escape') {
      setEditingCell(null);
    }
  };

  const handleComboboxConfirm = async () => {
    if (!editingCell) return;
    
    const { stakeholderId, field, value: originalValue } = editingCell;
    
    // No change, just cancel
    if (editingValue === originalValue) {
      setEditingCell(null);
      return;
    }

    try {
      const updatePayload: any = { [field]: editingValue || null };
      
      const { error } = await supabase
        .from('stakeholders')
        .update(updatePayload)
        .eq('id', stakeholderId);

      if (error) throw error;

      // Optimistic update instead of refetch
      updateStakeholderCache(stakeholderId, field, editingValue || null);
      toast.success('Updated successfully');
      setEditingCell(null);
    } catch (error) {
      console.error('Combobox edit error:', error);
      toast.error('Failed to update');
      setEditingCell(null);
    }
  };

  const handleComboboxCancel = () => {
    setEditingCell(null);
  };

  // Direct status change handler
  const handleStatusChange = async (stakeholderId: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from('stakeholders')
        .update({ status: newStatus })
        .eq('id', stakeholderId);

      if (error) throw error;

      // Optimistic update
      updateStakeholderCache(stakeholderId, 'status', newStatus);
      toast.success('Status updated');
    } catch (error) {
      console.error('Status update error:', error);
      toast.error('Failed to update status');
    } finally {
      setEditingCell(null);
    }
  };

  const renderEditableCell = (stakeholder: Stakeholder, field: string, value: any) => {
    const isEditing = editingCell?.stakeholderId === stakeholder.id && editingCell?.field === field;
    
    if (isEditing) {
      // Use CreatableCombobox for project_group and project_type
      if (field === 'project_group' || field === 'project_type') {
        const options = field === 'project_group' ? projectGroups : projectTypes;
        
        return (
          <CreatableCombobox
            value={editingValue}
            onChange={setEditingValue}
            onConfirm={handleComboboxConfirm}
            onCancel={handleComboboxCancel}
            options={options}
            placeholder={field === 'project_group' ? 'Select or type group' : 'Select or type type'}
          />
        );
      }
      
      // Use Select for status field
      if (field === 'status') {
        return (
          <Select
            value={editingValue || 'hold'}
            onValueChange={(newValue) => handleStatusChange(stakeholder.id, newValue)}
          >
            <SelectTrigger className="h-8 w-[110px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((opt) => {
                const Icon = opt.icon;
                return (
                  <SelectItem key={opt.value} value={opt.value}>
                    <span className="flex items-center gap-1.5">
                      {Icon && <Icon className="h-3 w-3" />}
                      {opt.label}
                    </span>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        );
      }
      
      // Use Textarea for notes field
      if (field === 'notes') {
        return (
          <Textarea
            autoFocus
            value={editingValue}
            onChange={(e) => setEditingValue(e.target.value)}
            onBlur={handleCellBlur}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setEditingCell(null);
            }}
            className="min-h-[80px] min-w-[200px] text-sm"
            placeholder="Enter notes..."
          />
        );
      }
      
      // Use Input for project field (text-only project name)
      if (field === 'project') {
        return (
          <Input
            autoFocus
            value={editingValue}
            onChange={(e) => setEditingValue(e.target.value)}
            onBlur={handleCellBlur}
            onKeyDown={handleCellKeyDown}
            className="h-8 min-w-[150px]"
            placeholder="Enter project name..."
          />
        );
      }
      
      // Use regular Input for other fields
      return (
        <Input
          autoFocus
          value={editingValue}
          onChange={(e) => setEditingValue(e.target.value)}
          onBlur={handleCellBlur}
          onKeyDown={handleCellKeyDown}
          className="h-8 px-2 py-1"
          type={field === 'email' ? 'email' : 'text'}
        />
      );
    }

    // For status field, render Badge that's clickable
    if (field === 'status') {
      const status = (value || DEFAULT_STATUS) as StakeholderStatus;
      const config = STATUS_CONFIG[status] || STATUS_CONFIG[DEFAULT_STATUS];
      const Icon = config.icon;
      return (
        <Badge
          variant={config.activeClassName ? undefined : config.variant}
          className={`text-xs cursor-pointer ${config.activeClassName ?? ''} ${canEdit ? 'hover:ring-1 hover:ring-primary/50' : ''}`}
          onClick={() => handleCellClick(stakeholder, field)}
          title={canEdit ? 'Click to change status' : ''}
        >
          <span className="flex items-center gap-1">
            {Icon && <Icon className="h-3 w-3" />}
            {config.label}
          </span>
        </Badge>
      );
    }

    return (
      <div
        onClick={() => handleCellClick(stakeholder, field)}
        className={`cursor-pointer hover:bg-accent/50 rounded px-2 py-1 min-h-[32px] flex items-center ${
          canEdit ? 'hover:ring-1 hover:ring-primary/50' : ''
        }`}
        title={canEdit ? 'Click to edit' : ''}
      >
        {value || '-'}
      </div>
    );
  };

  const handleCalendarInteraction = (date: Date) => {
    setSelectedDate(date);
    setPrefilledDate(date);
    setIsLogDialogOpen(true);
  };

  const handleAddEventClick = () => {
    setIsAddEventDialogOpen(true);
  };

  const handleCalendarFollowUp = (date: Date) => {
    setSelectedDate(date);
    setPrefilledDate(date);
    setIsFollowUpDialogOpen(true);
  };

  const handleEditEvent = (event: { type: 'interaction' | 'follow-up'; data: any }) => {
    if (event.type === 'follow-up') {
      setEditingFollowUp(event.data);
      editFollowUpDialog.open();
    } else {
      setEditingInteraction(event.data);
      editInteractionDialog.open();
    }
  };

  const handleDeleteInteraction = async (id: string) => {
    if (!canDelete) return;
    try {
      await deleteInteraction(id);
      toast.success('Tương tác đã được xóa');
      refetchInteractions();
    } catch (error) {
      toast.error('Không thể xóa tương tác');
    }
  };

  const handleDeleteFollowUp = async (id: string) => {
    if (!canDelete) return;
    try {
      await deleteFollowUp(id);
      toast.success('Follow-up đã được xóa');
      refetchFollowUps();
    } catch (error) {
      toast.error('Không thể xóa follow-up');
    }
  };

  // Filter helper functions
  const toggleLeadFilter = (leadId: string) => {
    setFilterLeads(prev => 
      prev.includes(leadId)
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  const toggleTypeFilter = (type: string) => {
    setFilterTypes(prev => 
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  const toggleStatusFilter = (status: string) => {
    setFilterStatuses(prev =>
      prev.includes(status)
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  };

  const clearAllFilters = () => {
    setFilterLeads([]);
    setFilterTypes([]);
    setFilterStatuses([]);
    setSearchQuery('');
  };

  const getActiveFiltersCount = () => {
    return filterLeads.length + filterTypes.length + filterStatuses.length + (searchQuery ? 1 : 0);
  };

  // Extract unique filter options
  const availableLeads = React.useMemo(() => {
    const uniqueLeads = Array.from(new Set(
      tableData.map(s => s.created_by).filter(Boolean)
    ));
    
    return uniqueLeads.map(leadId => {
      const stakeholder = tableData.find(s => s.created_by === leadId);
      return {
        id: leadId as string,
        name: stakeholder?.creator_name || 'Unknown'
      };
    });
  }, [tableData]);

  const availableTypes = React.useMemo(() => {
    return Array.from(new Set(
      tableData.map(s => s.project_type).filter(Boolean)
    )).sort() as string[];
  }, [tableData]);

  const openNoteDialog = (stakeholder: Stakeholder) => {
    setNoteViewStakeholder(stakeholder);
    setNoteDialogOpen(true);
  };

  const columns = [
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      className: 'min-w-[150px] whitespace-nowrap',
      render: (_value: any, row: Stakeholder) => (
        <div className="truncate max-w-[180px]" title={row.name}>
          {renderEditableCell(row, 'name', row.name)}
        </div>
      ),
    },
    {
      key: 'thumbnail',
      header: 'Image',
      sortable: false,
      className: 'w-[80px] text-center whitespace-nowrap',
      render: (_value: any, row: Stakeholder, index: number) => (
        <StakeholderThumbnail 
          attachments={row.attachments} 
          stakeholderId={row.id}
          index={index}
          editable={canEdit}
          onEdit={() => {
            setImageUploadStakeholder(row);
            setImageUploadOpen(true);
          }}
        />
      ),
    },
    {
      key: 'email',
      header: 'Email',
      sortable: true,
      className: 'min-w-[180px] whitespace-nowrap',
      render: (_value: any, row: Stakeholder) => (
        <div className="truncate max-w-[200px]" title={row.email || ''}>
          {renderEditableCell(row, 'email', row.email)}
        </div>
      ),
    },
    {
      key: 'phone',
      header: 'Phone',
      className: 'min-w-[120px] whitespace-nowrap',
      render: (_value: any, row: Stakeholder) => (
        <div className="truncate max-w-[140px]" title={row.phone || ''}>
          {renderEditableCell(row, 'phone', row.phone)}
        </div>
      ),
    },
    // Role column removed - only 1 type
    {
      key: 'project_group',
      header: 'Group',
      sortable: true,
      className: 'min-w-[100px] whitespace-nowrap',
      render: (_value: any, row: Stakeholder) => {
        if (!row.project_id) {
          return (
            <div className="truncate max-w-[120px]" title={row.project_group || ''}>
              {renderEditableCell(row, 'project_group', row.project_group)}
            </div>
          );
        }
        return (
          <span className={`truncate block max-w-[120px] ${row.project_group ? '' : 'text-muted-foreground italic'}`} title={row.project_group || ''}>
            {row.project_group || '-'}
          </span>
        );
      }
    },
    {
      key: 'project_type',
      header: 'Type',
      sortable: true,
      className: 'min-w-[120px] whitespace-nowrap',
      render: (_value: any, row: Stakeholder) => {
        if (!row.project_id) {
          return (
            <div className="truncate max-w-[140px]" title={row.project_type || ''}>
              {renderEditableCell(row, 'project_type', row.project_type)}
            </div>
          );
        }
        return (
          <span className={`truncate block max-w-[140px] ${row.project_type ? '' : 'text-muted-foreground italic'}`} title={row.project_type || ''}>
            {row.project_type || '-'}
          </span>
        );
      }
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      className: 'min-w-[110px] whitespace-nowrap',
      render: (value: string | null, row: Stakeholder) => renderEditableCell(row, 'status', value || 'hold')
    },
    {
      key: 'project_name',
      header: 'Project',
      sortable: true,
      className: 'min-w-[180px] whitespace-nowrap',
      render: (_value: string | null, row: Stakeholder) => {
        const linkedProject = row.project_name;
        const textProject = (row as any).text_project;
        
        if (row.project_id && linkedProject) {
          // Linked project - show with Link icon
          return (
            <div className="flex items-center gap-1.5 max-w-[200px]">
              <Link className="h-3 w-3 text-primary flex-shrink-0" />
              <span className="truncate" title={linkedProject}>{linkedProject}</span>
            </div>
          );
        }
        
        // Text-only project OR empty - editable
        return (
          <div className="truncate max-w-[200px]" title={textProject || ''}>
            {renderEditableCell(row, 'project', textProject)}
          </div>
        );
      }
    },
    {
      key: 'creator_name',
      header: 'Lead',
      sortable: true,
      className: 'min-w-[120px] whitespace-nowrap',
      render: (value: string | null | undefined) => (
        <span className={`truncate block max-w-[140px] ${value ? '' : 'text-muted-foreground italic'}`} title={value || ''}>
          {value || 'Unknown'}
        </span>
      )
    },
    {
      key: 'notes',
      header: 'Notes',
      sortable: false,
      className: 'w-[320px] min-w-[320px] max-w-[320px] align-top',
      render: (value: string, row: Stakeholder) => {
        const isEditing = editingCell?.stakeholderId === row.id && editingCell?.field === 'notes';
        if (isEditing) return renderEditableCell(row, 'notes', value);

        return (
          <div
            onClick={() => handleCellClick(row, 'notes')}
            className={`cursor-pointer hover:bg-accent/50 rounded px-2 py-1 min-h-[32px] whitespace-normal break-words line-clamp-3 text-sm ${
              canEdit ? 'hover:ring-1 hover:ring-primary/50' : ''
            }`}
            title={canEdit ? 'Click to edit' : value || ''}
          >
            {value || '-'}
          </div>
        );
      },
    },
  ];

  const actions = [
    ...(canEdit ? [
      buildEditAction<Stakeholder>(handleEdit),
      {
        label: 'Assign to Project',
        icon: <Link className="h-4 w-4" />,
        onClick: (stakeholder: Stakeholder) => {
          setSelectedStakeholder(stakeholder);
          setStandardValue(ASSIGN_SELECTION_KEY, stakeholder.id);
          setIsAssignDialogOpen(true);
        },
        disabled: (stakeholder: Stakeholder) => !!stakeholder.project_id,
        variant: 'ghost' as const,
      }
    ] : []),
    ...(canDelete ? [buildDeleteAction<Stakeholder>(handleDelete)] : []),
  ];

  if (!canViewList) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center min-h-[400px]">
          <p className="text-muted-foreground">You don't have permission to view stakeholders list</p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center space-y-2">
            <p className="text-destructive">Failed to load stakeholders</p>
            <p className="text-sm text-muted-foreground">{error?.message || 'Unknown error'}</p>
            <Button onClick={() => refetch()} variant="outline">
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto py-6 space-y-6">
        {/* CRM Dashboard Section */}
        {metrics && (
          <div className="space-y-4">
            <MetricsCards {...metrics} />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:items-stretch">
              <div className="md:col-span-2">
                <StakeholderCalendar 
                  stakeholders={crmStakeholders} 
                  interactions={interactions as CRMInteraction[]}
                  followUps={followUps as CRMFollowUp[]}
                  selectedDate={selectedDate}
                  onDateSelect={handleDateSelect}
                  onAddInteraction={handleCalendarInteraction}
                  onAddFollowUp={handleCalendarFollowUp}
                  onEditEvent={handleEditEvent}
                />
              </div>
              <div className="md:col-span-1">
                <StakeholderEventsList 
                  stakeholders={crmStakeholders} 
                  interactions={interactions as CRMInteraction[]}
                  followUps={followUps as CRMFollowUp[]}
                  selectedDate={selectedDate}
                />
              </div>
            </div>
          </div>
        )}
        
        {/* Tabs: Stakeholders & Events */}
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)} className="space-y-4">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="stakeholders">Stakeholders</TabsTrigger>
            <TabsTrigger value="events">Events</TabsTrigger>
          </TabsList>
          
          {/* Tab Content: Stakeholders List */}
          <TabsContent value="stakeholders" className="space-y-4">
            {/* Consolidated Header + Filters Card */}
            <Card>
              <CardContent className="p-4 space-y-4">
                
                {/* Row 1: Title + Add Button + View Toggle */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Stakeholders</h1>
                    <p className="text-muted-foreground text-sm sm:text-base">
                      Manage stakeholders list
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {/* View Toggle */}
                    <div className="inline-flex border rounded-lg p-1 bg-muted/50">
                      <Button
                        variant={viewMode === 'table' ? 'default' : 'ghost'}
                        size="sm"
                        onClick={() => setViewMode('table')}
                        className="gap-2"
                      >
                        <TableIcon className="h-4 w-4" />
                        Table
                      </Button>
                      <Button
                        variant={viewMode === 'grid' ? 'default' : 'ghost'}
                        size="sm"
                        onClick={() => setViewMode('grid')}
                        className="gap-2"
                      >
                        <LayoutGrid className="h-4 w-4" />
                        Grid
                      </Button>
                    </div>
                    
                    {/* Add Button */}
                    {canCreate && (
                      <Button onClick={addDialog.open} className="flex items-center gap-2">
                        <Plus className="h-4 w-4" />
                        <span className="hidden sm:inline">Add Stakeholder</span>
                      </Button>
                    )}
                  </div>
                </div>

                {/* Divider */}
                <Separator />

                {/* Row 2: Filters Section */}
                <div className="space-y-3">
                  {/* Lead Filter */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-medium text-muted-foreground">
                        Lead (SOL/SOLB):
                      </span>
                      {filterLeads.length > 0 && (
                        <Badge variant="secondary" className="text-xs">
                          {filterLeads.length}
                        </Badge>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {availableLeads.map((lead) => (
                        <Badge
                          key={lead.id}
                          variant={filterLeads.includes(lead.id) ? 'default' : 'outline'}
                          className="cursor-pointer transition-colors"
                          onClick={() => toggleLeadFilter(lead.id)}
                        >
                          {lead.name}
                          {filterLeads.includes(lead.id) && (
                            <X className="ml-1 h-3 w-3" />
                          )}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  {/* Global Search */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-medium text-muted-foreground">
                        Tìm kiếm:
                      </span>
                      {searchQuery && (
                        <Badge variant="secondary" className="text-xs">
                          "{searchQuery}"
                        </Badge>
                      )}
                    </div>
                    <div className="relative max-w-sm">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Tìm tên, email, phone, dự án, notes..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-9 pr-9"
                      />
                      {searchQuery && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                          onClick={() => setSearchQuery('')}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Type Filter */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-medium text-muted-foreground">
                        Loại dự án:
                      </span>
                      {filterTypes.length > 0 && (
                        <Badge variant="secondary" className="text-xs">
                          {filterTypes.length}
                        </Badge>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {availableTypes.map((type) => (
                        <Badge
                          key={type}
                          variant={filterTypes.includes(type) ? 'default' : 'outline'}
                          className="cursor-pointer transition-colors"
                          onClick={() => toggleTypeFilter(type)}
                        >
                          {type}
                          {filterTypes.includes(type) && (
                            <X className="ml-1 h-3 w-3" />
                          )}
                        </Badge>
                       ))}
                    </div>
                  </div>

                  {/* Status Filter */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-medium text-muted-foreground">
                        Trạng thái:
                      </span>
                      {filterStatuses.length > 0 && (
                        <Badge variant="secondary" className="text-xs">
                          {filterStatuses.length}
                        </Badge>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {STATUS_OPTIONS.map((s) => {
                        const isActive = filterStatuses.includes(s.value);
                        const Icon = s.icon;
                        return (
                          <Badge
                            key={s.value}
                            variant={isActive && !s.activeClassName ? 'default' : 'outline'}
                            className={`cursor-pointer transition-colors ${isActive && s.activeClassName ? s.activeClassName : ''}`}
                            onClick={() => toggleStatusFilter(s.value)}
                          >
                            <span className="flex items-center gap-1">
                              {Icon && <Icon className="h-3 w-3" />}
                              {s.label}
                              {isActive && <X className="ml-1 h-3 w-3" />}
                            </span>
                          </Badge>
                        );
                      })}
                    </div>
                  </div>


                  {getActiveFiltersCount() > 0 && (
                    <div className="flex items-center justify-between pt-2 border-t">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <span>Đang hiển thị {filteredData.length} / {tableData.length} stakeholders</span>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={clearAllFilters}
                        className="h-8"
                      >
                        Xóa tất cả
                      </Button>
                    </div>
                  )}
                </div>
                
              </CardContent>
            </Card>

            {/* Data container */}
            <div ref={scrollContainerRef}>
              {(() => {
                const effectiveIsLoading = isLoading || status === 'pending' || fetchStatus === 'fetching';
                
                if (effectiveIsLoading) {
                  return (
                    <div className="flex items-center justify-center h-[400px]">
                      <div className="text-muted-foreground">Loading stakeholders...</div>
                    </div>
                  );
                }
                
                if (filteredData.length === 0) {
                  return (
                    <div className="flex items-center justify-center h-[400px]">
                      <div className="text-muted-foreground">No stakeholders match the filters</div>
                    </div>
                  );
                }

                return (
                  <>
                    {viewMode === 'table' ? (
                      <ScrollArea className="w-full">
                        <div className="min-w-max">
                          <Table key={`stakeholders-${dataUpdatedAt ?? 0}-${stakeholders?.length ?? 0}`}>
                            <TableHeader>
                              <TableRow>
                                {columns.map((col, idx) => (
                                  <TableHead key={idx} className={col.className}>
                                    {col.header}
                                  </TableHead>
                                ))}
                                {(canEdit || canDelete) && (
                                  <TableHead className="w-[100px] text-center whitespace-nowrap">Actions</TableHead>
                                )}
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {paginatedData.map((stakeholder, index) => (
                                <TableRow 
                                  key={stakeholder.id}
                                  className="hover:bg-muted/50"
                                >
                                  {columns.map((col, colIdx) => (
                                    <TableCell key={colIdx} className={col.className}>
                                      {col.render 
                                        ? col.render(stakeholder[col.key], stakeholder, index)
                                        : stakeholder[col.key] || '-'}
                                    </TableCell>
                                  ))}
                                  {(canEdit || canDelete) && (
                                    <TableCell className="text-center whitespace-nowrap">
                                      <div className="flex gap-1 justify-center">
                                        {canEdit && (
                                          <Button 
                                            variant="ghost" 
                                            size="sm" 
                                            onClick={() => handleEdit(stakeholder)}
                                            title="Edit attachments, interactions, and history"
                                          >
                                            <Edit className="h-4 w-4" />
                                          </Button>
                                        )}
                                        {canEdit && stakeholder.project_id === null && (
                                          <Button 
                                            variant="ghost" 
                                            size="sm" 
                                            onClick={() => {
                                              setSelectedStakeholder(stakeholder);
                                              setStandardValue(ASSIGN_SELECTION_KEY, stakeholder.id);
                                              setIsAssignDialogOpen(true);
                                            }}
                                          >
                                            <Link className="h-4 w-4" />
                                          </Button>
                                        )}
                                        {canDelete && (
                                          <Button variant="ghost" size="sm" onClick={() => handleDelete(stakeholder)}>
                                            <Trash2 className="h-4 w-4" />
                                          </Button>
                                        )}
                                      </div>
                                    </TableCell>
                                  )}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                        <ScrollBar orientation="horizontal" />
                      </ScrollArea>
                    ) : (
                      <StakeholderGridView
                        stakeholders={paginatedData}
                        canEdit={canEdit}
                        canDelete={canDelete}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onAssign={(stakeholder) => {
                          setSelectedStakeholder(stakeholder);
                          setStandardValue(ASSIGN_SELECTION_KEY, stakeholder.id);
                          setIsAssignDialogOpen(true);
                        }}
                        onViewNote={openNoteDialog}
                        onStatusChange={handleStatusChange}
                        onImageEdit={(stakeholder) => {
                          setImageUploadStakeholder(stakeholder);
                          setImageUploadOpen(true);
                        }}
                        onProjectEdit={(stakeholder) => handleCellClick(stakeholder, 'project')}
                      />
                    )}
                    
                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4 px-3 sm:px-4 py-3 border-t">
                        <div className="text-xs sm:text-sm text-muted-foreground text-center sm:text-left">
                          Showing {startItem} to {endItem} of {filteredData.length} stakeholders
                        </div>
                        <div className="overflow-x-auto max-w-full">
                          <Pagination>
                            <PaginationContent className="flex-wrap justify-center">
                              <PaginationItem>
                                <PaginationPrevious 
                                  onClick={() => hasPreviousPage && setPage(currentPage - 1)}
                                  className={!hasPreviousPage ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                                />
                              </PaginationItem>
                              
                              {generatePageNumbers().map((page, idx) => (
                                <PaginationItem key={idx}>
                                  {page === 'ellipsis' ? (
                                    <PaginationEllipsis />
                                  ) : (
                                    <PaginationLink
                                      onClick={() => setPage(page)}
                                      isActive={currentPage === page}
                                      className="cursor-pointer"
                                    >
                                      {page}
                                    </PaginationLink>
                                  )}
                                </PaginationItem>
                              ))}
                              
                              <PaginationItem>
                                <PaginationNext 
                                  onClick={() => hasNextPage && setPage(currentPage + 1)}
                                  className={!hasNextPage ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                                />
                              </PaginationItem>
                            </PaginationContent>
                          </Pagination>
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </TabsContent>
          
          {/* Tab Content: Events */}
          <TabsContent value="events" className="space-y-4">
            <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Events</h1>
                <p className="text-muted-foreground text-sm sm:text-base">
                  Tất cả sự kiện tương tác và follow-ups
                </p>
              </div>
            </div>

            <EventsTable
              stakeholders={crmStakeholders}
              interactions={interactions as CRMInteraction[]}
              followUps={followUps as CRMFollowUp[]}
              onEditInteraction={(interaction) => {
                setEditingInteraction(interaction);
                editInteractionDialog.open();
              }}
              onEditFollowUp={(followUp) => {
                setEditingFollowUp(followUp);
                editFollowUpDialog.open();
              }}
              onDeleteInteraction={handleDeleteInteraction}
              onDeleteFollowUp={handleDeleteFollowUp}
              onAddEvent={handleAddEventClick}
              canEdit={canEdit}
              canDelete={canDelete}
              canCreate={canCreate}
            />
          </TabsContent>
        </Tabs>

        {canCreate && (
          <StakeholderAddDialog
            isOpen={addDialog.isOpen}
            onClose={addDialog.close}
            onSuccess={() => {
              refetch();
              addDialog.close();
            }}
          />
        )}

        {canEdit && (
          <StakeholderEditDialog
            stakeholder={selectedStakeholder}
            isOpen={editDialog.isOpen}
            onClose={handleCloseEdit}
            onSuccess={() => {
              refetch();
              handleCloseEdit();
            }}
          />
        )}
        
        {isLogDialogOpen && (
          <LogInteractionDialog
            stakeholders={crmStakeholders}
            preselectedStakeholder={null}
            isOpen={isLogDialogOpen}
            onClose={() => {
              setIsLogDialogOpen(false);
              setPrefilledDate(undefined);
            }}
            onSuccess={() => {
              refetchInteractions();
              setIsLogDialogOpen(false);
            }}
            prefilledDate={prefilledDate}
          />
        )}
        
        {isFollowUpDialogOpen && (
          <AddFollowUpDialog
            stakeholders={crmStakeholders}
            preselectedStakeholder={null}
            isOpen={isFollowUpDialogOpen}
            onClose={() => {
              setIsFollowUpDialogOpen(false);
              setPrefilledDate(undefined);
            }}
            onSuccess={() => {
              refetch();
              refetchFollowUps();
              setIsFollowUpDialogOpen(false);
            }}
            prefilledDate={prefilledDate}
          />
        )}
        
        {editFollowUpDialog.isOpen && (
          <EditFollowUpDialog
            followUp={editingFollowUp}
            stakeholders={crmStakeholders}
            isOpen={editFollowUpDialog.isOpen}
            onClose={() => {
              editFollowUpDialog.close();
              setEditingFollowUp(null);
            }}
            onSuccess={() => {
              refetch();
              refetchFollowUps();
              editFollowUpDialog.close();
              setEditingFollowUp(null);
              toast.success('Follow-up đã được cập nhật');
            }}
          />
        )}

        {editInteractionDialog.isOpen && (
          <EditInteractionDialog
            interaction={editingInteraction}
            stakeholders={crmStakeholders}
            isOpen={editInteractionDialog.isOpen}
            onClose={() => {
              editInteractionDialog.close();
              setEditingInteraction(null);
            }}
            onSuccess={() => {
              refetchInteractions();
              editInteractionDialog.close();
              setEditingInteraction(null);
              toast.success('Tương tác đã được cập nhật');
            }}
          />
        )}
        
        {canEdit && (
          <StakeholderAssignDialog
            stakeholder={selectedStakeholder}
            isOpen={isAssignDialogOpen}
            onClose={() => {
              removeStandardValue(ASSIGN_SELECTION_KEY);
              setSelectedStakeholder(null);
              setIsAssignDialogOpen(false);
            }}
            onSuccess={() => {
              refetch();
              removeStandardValue(ASSIGN_SELECTION_KEY);
              setSelectedStakeholder(null);
              setIsAssignDialogOpen(false);
            }}
          />
        )}

        <DeleteConfirmDialog
          isOpen={deleteDialogOpen}
          onClose={() => {
            setDeleteDialogOpen(false);
            setStakeholderToDelete(null);
          }}
          onConfirm={handleConfirmDelete}
          itemName={stakeholderToDelete?.name || ''}
          itemType="stakeholder"
          isLoading={deleteStakeholder.isPending}
        />

        {/* Add Event Dialog */}
        <Dialog open={isAddEventDialogOpen} onOpenChange={setIsAddEventDialogOpen}>
          <DialogContent className="sm:max-w-[400px]">
            <DialogHeader>
              <DialogTitle>Add New Event</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-4">
              <Button
                variant="outline"
                className="w-full justify-start h-auto py-4"
                onClick={() => {
                  setIsAddEventDialogOpen(false);
                  setIsLogDialogOpen(true);
                }}
              >
                <MessageSquare className="mr-3 h-5 w-5" />
                <div className="text-left">
                  <div className="font-medium">Log Interaction</div>
                  <div className="text-xs text-muted-foreground">
                    Record a completed meeting, call, or visit
                  </div>
                </div>
              </Button>
              
              <Button
                variant="outline"
                className="w-full justify-start h-auto py-4"
                onClick={() => {
                  setIsAddEventDialogOpen(false);
                  setIsFollowUpDialogOpen(true);
                }}
              >
                <Calendar className="mr-3 h-5 w-5" />
                <div className="text-left">
                  <div className="font-medium">Schedule Follow-up</div>
                  <div className="text-xs text-muted-foreground">
                    Plan a future action or reminder
                  </div>
                </div>
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Note View Dialog */}
        <Dialog open={noteDialogOpen} onOpenChange={setNoteDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Notes - {noteViewStakeholder?.name}</DialogTitle>
            </DialogHeader>
            <div className="whitespace-pre-wrap text-sm max-h-[60vh] overflow-auto p-4 bg-muted/30 rounded-md">
              {noteViewStakeholder?.notes || 'No notes'}
            </div>
          </DialogContent>
        </Dialog>

        {/* Image Upload Dialog */}
        {imageUploadStakeholder && (
          <StakeholderImageUploadDialog
            open={imageUploadOpen}
            onOpenChange={(open) => {
              setImageUploadOpen(open);
              if (!open) setImageUploadStakeholder(null);
            }}
            stakeholderId={imageUploadStakeholder.id}
            currentAttachments={imageUploadStakeholder.attachments}
            onSuccess={(newAttachments) => {
              // Optimistic update
              updateStakeholderCache(imageUploadStakeholder.id, 'attachments', newAttachments);
            }}
          />
        )}
      </main>
    </div>
  );
}